import type { AppThunk } from '../store';
import type { SubscriptionFrequency } from '../store/structureDefinitions/checkoutState';
import type { DeliveryMode } from '../store/structureDefinitions/storeState';
import type { InvoiceAddress } from './user/userModels';

import { appConfig } from '../../../appConfig';
import { appInsights } from '../../../appInsights';
import ecomStoreFlow from '../../ecommerce/flow/ecomStoreFlow';
import { getAuthToken } from '../api/auth/getAuthToken';
import cartApi from '../api/cart/cartApi';
import directCartApi from '../api/cart/directCartApi';
import { hybrisUserDirectApi } from '../api/user/hybrisUser/hybrisUserDirectApi';
import { isLocalApiBaseException } from '../exceptions/exceptionUtils';
import { checkoutActions } from '../store/slices/checkout/checkoutSlice';
import { subscriptionFrequencyActions } from '../store/slices/checkout/subscriptionSlice';
import { currentCartFlow } from './cart/currentCartFlow';
import userFlow from './user/userFlow';

const selectDeliveryAddress = async (addressId: string) => {
    const result = await cartApi.deliveryAddresses.select(addressId);
    return result;
};

const setHomeDeliveryAddress = async (zipCode: string, addressId: string, baseStoreId?: string) => {
    await ecomStoreFlow.changeDeliveryStore({ postalCode: zipCode, baseStoreId, force: true });
    const result = await cartApi.deliveryAddresses.select(addressId);
    return result.id;
};

const saveBillingAddress = async (billingAddress: InvoiceAddress) => {
    const { token, shoppingUserId } = await getAuthToken();
    const hybrisUserInfoWithInvoice = await hybrisUserDirectApi.createInvoiceAddress(token)(
        shoppingUserId,
        billingAddress,
    );

    if (!hybrisUserInfoWithInvoice.billingAddress) {
        throw new Error('No billing address returned from createInvoiceAddress');
    }

    await cartApi.billingAddress.select(hybrisUserInfoWithInvoice.billingAddress.id);
    return hybrisUserInfoWithInvoice.billingAddress;
};

const updateBillingAddress = async (billingAddress: InvoiceAddress) => {
    const { token, shoppingUserId } = await getAuthToken();
    await hybrisUserDirectApi.updateInvoiceAddress(token)(shoppingUserId, billingAddress);
    await cartApi.billingAddress.select(billingAddress.id);
};

const getCurrentBillingAddress = async () => {
    const billingAddress = await cartApi.billingAddress.current();
    return billingAddress;
};

const loadAndSelectBillingAddress = async () => {
    const selectedBillingAddress = await getCurrentBillingAddress();
    if (selectedBillingAddress) {
        return selectedBillingAddress;
    }

    const billingAddress = await userFlow.getInvoiceAddress();
    if (billingAddress) {
        await cartApi.billingAddress.select(billingAddress.id);
    }
    return billingAddress;
};

const setSubscription = (week: SubscriptionFrequency | null): AppThunk => {
    return async (dispatch) => {
        dispatch(subscriptionFrequencyActions.set(week));
    };
};

const fetchMessageToDriver = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    const cartId = await currentCartFlow.getCurrentCartId();
    if (!cartId) {
        const error = new Error('No active cart!');
        appInsights.trackException(
            { exception: error },
            { place: 'checkoutFlow.fetchMessageToDriver' },
        );
        throw error;
    }
    const { driverMessage } = await cartApi.driverMessage.get(token)({
        userId: shoppingUserId,
        cartId,
    });
    return driverMessage;
};

const updateMessageToDriver = async (msg: string) => {
    const { token, shoppingUserId } = await getAuthToken();
    const cartId = await currentCartFlow.getCurrentCartId();
    if (!cartId) {
        const error = new Error('No active cart!');
        appInsights.trackException(
            { exception: error },
            { place: 'checkoutFlow.updateMessageToDriver' },
        );
        throw error;
    }
    await cartApi.driverMessage.put(token)({
        userId: shoppingUserId,
        cartId,
        message: msg,
    });
    return msg;
};

const resetDeliveryStep = (): AppThunk => {
    return async (dispatch) => {
        dispatch(checkoutActions.reset());
    };
};

const setDeliveryTab = (mode: DeliveryMode | null): AppThunk => {
    return async (dispatch) => {
        dispatch(checkoutActions.deliveryTabActions.set(mode));
    };
};

const fetchDeliveryAddress = async () => {
    try {
        const deliveryAddress = await cartApi.deliveryAddresses.current();
        return deliveryAddress;
    } catch (err) {
        if (isLocalApiBaseException(err)) {
            return Promise.reject(err.friendlyMessage);
        }
        return Promise.reject(appConfig.coopSettings.ecommerce.errorMessage.general);
    }
};

const isValidDate = (d: Date) => {
    return d instanceof Date && !Number.isNaN(d);
};

const getRequisitionExpireTime = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    const { requisitionExpireTime } =
        await cartApi.punchout.getRequisitionExpireTime(token)(shoppingUserId);
    if (!requisitionExpireTime) return null;
    const date = new Date(requisitionExpireTime);
    if (!isValidDate(date)) return null;

    return date.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' });
};

const getInternalReference = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    const { internalReference } =
        await cartApi.punchout.getInternalReference(token)(shoppingUserId);
    return internalReference;
};

const setInternalReference = async (internalReference: string) => {
    const { token, shoppingUserId } = await getAuthToken();
    await cartApi.punchout.setInternalReference(token)(shoppingUserId, internalReference);
};

const getContactPhoneNumber = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    const data = await cartApi.punchout.getContactPhoneNumber(token)(shoppingUserId);
    return 'phone' in data ? data.phone : undefined;
};

const setContactPhoneNumber = async (phone: string) => {
    const { token, shoppingUserId } = await getAuthToken();
    await cartApi.punchout.setContactPhoneNumber(token)(shoppingUserId, phone);
};

const createPunchout = async () => {
    const { token, shoppingUserId } = await getAuthToken();

    // will throw on failure
    await cartApi.punchout.validateCart({ token, userId: shoppingUserId });
    const response = await cartApi.punchout.create(token)(shoppingUserId);
    return response;
};
const initialize = async () => {
    const cartId = await currentCartFlow.getCurrentCartId();

    // should never happen
    if (!cartId) {
        const error = new Error('No active cart!');
        appInsights.trackException({ exception: error }, { place: 'checkoutFlow.initialize' });
        throw error;
    }

    const { token, shoppingUserId } = await getAuthToken();

    const response = await directCartApi.initializeCheckout(shoppingUserId, cartId, token);
    return response;
};

const punchout = {
    setInternalReference,
    getInternalReference,
    create: createPunchout,
    getRequisitionExpireTime,
    getContactPhoneNumber,
    setContactPhoneNumber,
};

const deliveryAddress = {
    fetch: fetchDeliveryAddress,
    select: selectDeliveryAddress,
};

const billingAddress = {
    loadAndSelect: loadAndSelectBillingAddress,
    save: saveBillingAddress,
    update: updateBillingAddress,
};

const subscription = {
    set: setSubscription,
};

const messageToDriver = {
    fetch: fetchMessageToDriver,
    update: updateMessageToDriver,
};

export const checkoutFlow = {
    initialize,
    deliveryAddress,
    billingAddress,
    setHomeDeliveryAddress,
    subscription,
    messageToDriver,
    resetDeliveryStep,
    punchout,
    setDeliveryTab,
};
